import { useCallback, useEffect, useState } from "react";

import { submit, whoAmI } from "../../lib/runtime";
import { useCanvasState } from "../../lib/useCanvasState";
import { Joystick } from "./Joystick";
import { barEmotesOf, entrantsOf, type Direction } from "./stage";

/** A way in, and once in, a way to walk and a way to emote. Three mounts, three activities. */
export function DropViewer({
  dropMount,
  padMount,
  emoteMount,
}: {
  dropMount: string;
  padMount: string;
  emoteMount: string;
}) {
  const roster = useCanvasState(dropMount);
  const bar = useCanvasState(emoteMount);
  const [me, setMe] = useState<string | null>(null);
  const [emote, setEmote] = useState<string>("");
  const [refused, setRefused] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    whoAmI()
      .then((who) => {
        if (live) setMe(who.account_id);
      })
      .catch((error: unknown) => {
        if (live) setRefused(error instanceof Error ? error.message : "not signed in");
      });
    return () => {
      live = false;
    };
  }, []);

  const emotes = barEmotesOf(bar.state);
  const droppedIn = me !== null && entrantsOf(roster.state).includes(me);

  useEffect(() => {
    if (emote === "" && emotes.length > 0) setEmote(emotes[0] ?? "");
  }, [emote, emotes]);

  const send = useCallback((mount: string, submission: string, value: string) => {
    setRefused(null);
    submit(mount, submission, value).catch((error: unknown) => {
      setRefused(error instanceof Error ? error.message : "that was refused");
    });
  }, []);

  const step = useCallback(
    (direction: Direction) => send(padMount, "step", direction),
    [padMount, send],
  );

  if (roster.error !== null) return <DropProblem what={roster.error} who="viewer" />;

  return (
    <main
      style={{
        position: "fixed",
        inset: 0,
        background: "#0b0d10",
        color: "#e8eaed",
        font: "14px ui-monospace, monospace",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 26,
        padding: 20,
      }}
    >
      {!droppedIn ? (
        <>
          <p style={{ margin: 0, color: "rgba(255,255,255,0.6)" }}>you are not on the stage</p>
          <button
            type="button"
            onClick={() => send(dropMount, "presence", "in")}
            style={{ ...BUTTON, background: "#3ddc84", color: "#0b0d10", fontSize: 20 }}
          >
            drop in
          </button>
        </>
      ) : (
        <>
          <Joystick onStep={step} size={220} />

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14 }}>
            <select
              aria-label="emote"
              value={emote}
              onChange={(event) => setEmote(event.target.value)}
              style={{
                padding: "10px 12px",
                background: "#1b2027",
                color: "inherit",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 4,
                font: "inherit",
              }}
            >
              {emotes.map((one) => (
                <option key={one} value={one}>
                  {LABEL[one] ?? one}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={emote === ""}
              onClick={() => send(emoteMount, "emote", emote)}
              style={{ ...BUTTON, background: emote === "" ? "#2a2f36" : "#4a7dff" }}
            >
              play it
            </button>
          </div>

          <button
            type="button"
            onClick={() => send(dropMount, "presence", "out")}
            style={{ ...BUTTON, background: "#3a424c" }}
          >
            drop out
          </button>
        </>
      )}

      <p style={{ margin: 0, minHeight: 18, color: refused === null ? "transparent" : "#ff8a80" }}>
        {refused ?? "."}
      </p>
    </main>
  );
}

const LABEL: Record<string, string> = {
  wave: "\u{1F44B} wave",
  heart: "\u{2764}\u{FE0F} heart",
  laugh: "\u{1F602} laugh",
};

const BUTTON: React.CSSProperties = {
  padding: "12px 22px",
  color: "white",
  border: 0,
  borderRadius: 6,
  font: "inherit",
  cursor: "pointer",
};

export function DropProblem({ what, who }: { what: string; who: string }) {
  return (
    <main style={{ padding: 24, fontFamily: "monospace", color: "#eee", background: "#0b0d10" }}>
      <h1>Cannot read this stage</h1>
      <p>{what}</p>
      <p>Sign in first at /v1/auth/twitch/start?as={who}, then reload.</p>
    </main>
  );
}
